import { useI18n } from '@change-corgi/core-react/i18n';

import { useCreateUser, useIsSecureGuest } from '#src/app/shared/hooks/payments';
import { useSetPxGuestUserData } from '#src/app/shared/hooks/perimeterX';
import { useLoginState } from '#src/app/shared/hooks/session';
import type { PaymentType } from '#src/app/shared/utils/payments';

import { usePromoteFormData } from '../../../../../formContext/formData';
import { usePromoteFormState } from '../../../../../formContext/formState';
import { useUserInfo } from '../../../useUserInfo';

export function useCreateUserWrapper(): (paymentType: PaymentType) => Promise<boolean> {
	const {
		petition: { slug },
		featureConfigs: { doubleOptinEnabledNewUser, googleAuthEnabled },
		user: { email },
	} = usePromoteFormData();
	const isSecureGuest = useIsSecureGuest(doubleOptinEnabledNewUser);
	const loginState = useLoginState();
	const [, { clearLoading, setGeneralErrorMessage }] = usePromoteFormState();
	const createUser = useCreateUser();
	const { translate } = useI18n();
	const setPxGuestUserData = useSetPxGuestUserData();
	const userInfo = useUserInfo();

	return async function createUserWrapper(paymentType: PaymentType): Promise<boolean> {
		if (isSecureGuest !== false) {
			if (loginState === 'GUEST') await setPxGuestUserData(email);
			return true;
		}

		const path = `/p/${encodeURIComponent(slug)}/promote`;
		const result = await createUser({
			...userInfo(paymentType),
			googleAuthEnabled,
			path,
			signupContext: 'payment_promotion',
		});
		if (result.success) return true;

		if (!result.isUserAlreadyExistsError) {
			setGeneralErrorMessage(translate('corgi.co.promote.generic_payment_error'));
		} else if (paymentType === 'paypal') {
			setGeneralErrorMessage(translate('fe.errors.paypal.complete_transaction_after_login'));
		} else {
			clearLoading();
		}
		return false;
	};
}
